import type React from 'react'
import styles from './Skeleton.module.css'

/**
 * Loading skeleton — Figma S3 frame `skeleton` (7062:30058, 402×668).
 * Placeholder rectangles (`background/secondary` fill, shimmer = web
 * affordance §10) mirroring the Full layout. Exact geometry per spec
 * §5.3 / Figma metadata. No spinner (spec F2). a11y id
 * `station-details-loading`.
 *
 * Each block matches the Figma group y-offset + the rect x/y/w/h inside
 * it; `<Rect>` emits a `background/secondary` shimmer block.
 */
function Rect({
  x,
  y,
  w,
  h,
  radius = 8,
}: {
  x: number
  y: number
  w: number
  h: number
  radius?: number
}): React.ReactElement {
  return (
    <span
      data-skeleton-rect
      className={styles.rect}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        width: `${w}px`,
        height: `${h}px`,
        borderRadius: `${radius}px`,
      }}
    />
  )
}

export function Skeleton({
  loadingLabel = 'Loading station',
}: {
  /** Accessible label for the loading overlay. @defaultValue `'Loading station'` */
  loadingLabel?: string
} = {}): React.ReactElement {
  return (
    <div
      data-testid="station-details-loading"
      className={styles.skeleton}
      role="status"
      aria-busy="true"
      aria-label={loadingLabel}
    >
      {/* Header — group y0: circle 48@(16,16) · title 160×20@(76,19) · subtitle 100×14@(76,47) */}
      <Rect x={16} y={16} w={48} h={48} radius={24} />
      <Rect x={76} y={19} w={160} h={20} />
      <Rect x={76} y={47} w={100} h={14} />

      {/* Favourites — group y80: icon 28@(16,12)→(16,92) · label 140×16@(56,18)→(56,98) */}
      <Rect x={16} y={92} w={28} h={28} radius={14} />
      <Rect x={56} y={98} w={140} h={16} />

      {/* Upsell — group y132: card 370×100@(16,8)→(16,140) */}
      <Rect x={16} y={140} w={370} h={100} radius={16} />

      {/* Section header — group y248: bar 90×16@(16,16)→(16,264) */}
      <Rect x={16} y={264} w={90} h={16} />

      {/* Map — group y288: tile 370×180@(16,8)→(16,296) */}
      <Rect x={16} y={296} w={370} h={180} radius={16} />

      {/* Section header — group y476: bar 110×16@(16,16)→(16,492) */}
      <Rect x={16} y={492} w={110} h={16} />

      {/* Facilities — group y516: 2×2 grid, each icon 28@(col,row) + label 70×16
          Row 1 @y524: cellA icon@(16,524) label@(52,530); cellB icon@(205,524) label@(241,530)
          Row 2 @y564: cellA icon@(16,564) label@(52,570); cellB icon@(205,564) label@(241,570) */}
      <Rect x={16} y={524} w={28} h={28} radius={14} />
      <Rect x={52} y={530} w={70} h={16} />
      <Rect x={205} y={524} w={28} h={28} radius={14} />
      <Rect x={241} y={530} w={70} h={16} />
      <Rect x={16} y={564} w={28} h={28} radius={14} />
      <Rect x={52} y={570} w={70} h={16} />
      <Rect x={205} y={564} w={28} h={28} radius={14} />
      <Rect x={241} y={570} w={70} h={16} />

      {/* Report — group y592: circle 44@(16,24)→(16,616) · label 130×18@(72,37)→(72,629) */}
      <Rect x={16} y={616} w={44} h={44} radius={22} />
      <Rect x={72} y={629} w={130} h={18} />
    </div>
  )
}
